import { useState, type FormEvent } from 'react';
import { Button, Input, Modal, Select, Switch, type SelectOption } from '../../components';
import { ApiError } from '../../lib/api';
import type { UserOption } from '../leads/types';
import type { ClientPayload, ClientRow } from './types';

type FormState = {
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
  phone: string;
  company: string;
  country: string;
  address: string;
  date_of_birth: string;
  client_category: string;
  assigned_sales_user_id: string;
  assigned_pse_user_id: string;
  is_active: boolean;
};

function toFormState(row?: ClientRow): FormState {
  return {
    first_name: row?.first_name ?? '',
    last_name: row?.last_name ?? '',
    full_name: row?.full_name ?? '',
    email: row?.email ?? '',
    phone: row?.phone ?? '',
    company: row?.company ?? '',
    country: row?.country ?? '',
    address: row?.address ?? '',
    date_of_birth: row?.date_of_birth ?? '',
    client_category: row?.client_category ?? '',
    assigned_sales_user_id: row?.assigned_sales_user_id ?? '',
    assigned_pse_user_id: row?.assigned_pse_user_id ?? '',
    is_active: row?.is_active ?? true,
  };
}

function toPayload(f: FormState): ClientPayload {
  const s = (v: string) => (v.trim() === '' ? null : v.trim());
  return {
    first_name: s(f.first_name),
    last_name: s(f.last_name),
    full_name: f.full_name.trim() || 'Unnamed client',
    email: s(f.email),
    phone: s(f.phone),
    company: s(f.company),
    country: s(f.country),
    address: s(f.address),
    date_of_birth: s(f.date_of_birth),
    client_category: s(f.client_category),
    assigned_sales_user_id: s(f.assigned_sales_user_id),
    assigned_pse_user_id: s(f.assigned_pse_user_id),
    is_active: f.is_active,
  };
}

export function ClientFormModal({
  row,
  users,
  onClose,
  onSubmit,
}: {
  row?: ClientRow;
  users: UserOption[];
  onClose: () => void;
  onSubmit: (payload: ClientPayload) => Promise<void>;
}) {
  const [form, setForm] = useState<FormState>(() => toFormState(row));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const userOpts: SelectOption[] = users.map((u) => ({ value: u.id, label: u.full_name }));

  async function handle(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit(toPayload(form));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={row ? 'Edit client' : 'New client'} width={560}>
      <form onSubmit={handle}>
        <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Input
                id="c-first"
                label="First name"
                value={form.first_name}
                onChange={(v) => setForm({ ...form, first_name: v })}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Input
                id="c-last"
                label="Last name"
                value={form.last_name}
                onChange={(v) => setForm({ ...form, last_name: v })}
              />
            </div>
          </div>
          <Input
            id="c-fullname"
            label="Display name"
            value={form.full_name}
            onChange={(v) => setForm({ ...form, full_name: v })}
            required
            autoFocus
          />
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Input
                id="c-email"
                label="Email"
                type="email"
                value={form.email}
                onChange={(v) => setForm({ ...form, email: v })}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Input
                id="c-phone"
                label="Phone"
                value={form.phone}
                onChange={(v) => setForm({ ...form, phone: v })}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Input
                id="c-company"
                label="Company"
                value={form.company}
                onChange={(v) => setForm({ ...form, company: v })}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Input
                id="c-country"
                label="Country"
                value={form.country}
                onChange={(v) => setForm({ ...form, country: v })}
              />
            </div>
          </div>
          <Input
            id="c-address"
            label="Address"
            value={form.address}
            onChange={(v) => setForm({ ...form, address: v })}
          />
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Input
                id="c-dob"
                label="Date of birth"
                type="date"
                value={form.date_of_birth}
                onChange={(v) => setForm({ ...form, date_of_birth: v })}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Input
                id="c-cat"
                label="Client category"
                value={form.client_category}
                onChange={(v) => setForm({ ...form, client_category: v })}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Select
                id="c-sales"
                label="Assigned sales"
                placeholder="— unassigned —"
                value={form.assigned_sales_user_id}
                options={userOpts}
                onChange={(v) => setForm({ ...form, assigned_sales_user_id: v })}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Select
                id="c-pse"
                label="Assigned PSE"
                placeholder="— unassigned —"
                value={form.assigned_pse_user_id}
                options={userOpts}
                onChange={(v) => setForm({ ...form, assigned_pse_user_id: v })}
              />
            </div>
          </div>
          <div className="row">
            <Switch
              on={form.is_active}
              onChange={(v) => setForm({ ...form, is_active: v })}
              label="Active"
            />
            <span className="muted">Active</span>
          </div>
          {error && <div className="alert">{error}</div>}
        </div>
        <div
          className="card-header"
          style={{
            justifyContent: 'flex-end',
            borderTop: '1px solid var(--border)',
            borderBottom: 'none',
          }}
        >
          <Button onClick={onClose} disabled={submitting} type="button">
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
